import Api from '../api';
import { API_CONFIG } from '../config';

/**
 * Organization API Service
 * Handles organization structure and hierarchy API calls
 */

const organizationService = {
  /**
   * Get operating units
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios response with operating unit data
   */
  async getOperatingUnits(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/OperatingUnits', { params: queryParams });
  },

  /**
   * Get legal entities
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios response with legal entity data
   */
  async getLegalEntities(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/LegalEntities', { params: queryParams });
  },

  /**
   * Get companies
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios response with company data
   */
  async getCompanies(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/Companies', { params: queryParams });
  },

  /**
   * Get organization hierarchies
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios response with hierarchy data
   */
  async getOrganizationHierarchies(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/OrganizationHierarchies', { params: queryParams });
  },

  /**
   * Get cost centers
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios response with cost center data
   */
  async getCostCenters(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/CostCenters', { params: queryParams });
  },

  /**
   * Get business units
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios response with business unit data
   */
  async getBusinessUnits(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/BusinessUnits', { params: queryParams });
  },

  /**
   * Get value streams
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios response with value stream data
   */
  async getValueStreams(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/ValueStreams', { params: queryParams });
  },

  /**
   * Get worker-manager relations for an employee
   * Returns both the employee's manager and their direct reports
   * @param {string} personnelNumber - Employee personnel number
   * @returns {Promise} Axios response with manager relations
   */
  async getWorkerManagerRelations(personnelNumber) {
    console.log(`🔍 Fetching manager relations for: ${personnelNumber}`);

    const params = {
      $filter: `(PersonnelNumber eq '${personnelNumber}' or ManagerPersonnelNumber eq '${personnelNumber}')`,
      'cross-company': true,
    };

    try {
      const response = await Api.get('/data/EssWorkerManagerRelations', { params });
      console.log('✅ Manager relations fetched:', response.data?.value?.length || 0, 'records');
      return response;
    } catch (error) {
      console.error('❌ Error fetching manager relations:', error);
      throw error;
    }
  },

  /**
   * Get peers (employees who report to the same manager)
   * @param {string} managerPersonnelNumber - Manager's personnel number
   * @returns {Promise} Axios response with peer relations
   */
  async getPeerRelations(managerPersonnelNumber) {
    console.log(`🔍 Fetching peers for manager: ${managerPersonnelNumber}`);

    const params = {
      $filter: `ManagerPersonnelNumber eq '${managerPersonnelNumber}'`,
      'cross-company': true,
    };

    try {
      const response = await Api.get('/data/EssWorkerManagerRelations', { params });
      console.log('✅ Peer relations fetched:', response.data?.value?.length || 0, 'records');
      return response;
    } catch (error) {
      console.error('❌ Error fetching peer relations:', error);
      throw error;
    }
  },
};

export default organizationService;
